/**
 * /inventory/transfers — transfer + request analytics.
 *
 * Each tab owns its own query. Radix `TabsContent` only mounts the active tab,
 * so opening the page costs one request instead of five.
 */
import { useMemo } from 'react';
import { Truck } from 'lucide-react';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/Card';
import { EmptyState } from '@/components/ui/EmptyState';
import { ErrorState } from '@/components/ui/ErrorState';
import { Skeleton } from '@/components/ui/Skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/Tabs';
import { KPICard } from '@/components/charts/KPICard';
import { BarChart } from '@/components/charts/BarChart';
import { useFilters, rangeISO } from '@/state/filters';
import { fmt } from '@/lib/format';
import {
  useTransfersCycleTime,
  useTransfersDiscrepancies,
  useTransfersOverview,
  useTransfersRequests,
  useTransfersVendorReturns,
} from '@/api/bi/transfers';

function pct(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${(v * 100).toFixed(1)}%`;
}

function hours(v: number | null | undefined): string {
  if (v == null || Number.isNaN(v)) return '—';
  return `${v.toFixed(1)}h`;
}

interface TabProps {
  filters: { start: string; end: string; storeId: number | null };
}

export function TransfersPage() {
  const { range, storeId, timeRangeMode } = useFilters();
  const iso = useMemo(() => rangeISO(range, timeRangeMode), [range, timeRangeMode]);
  const filters = { start: iso.start, end: iso.end, storeId: storeId ?? null };

  return (
    <>
      <PageHeader
        eyebrow="Inventory"
        title="Transfers"
        description="Warehouse ↔ darkstore moves, request funnel, cycle time, and vendor returns."
      />

      <Tabs defaultValue="overview">
        <TabsList className="mb-3">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="cycle-time">Cycle time</TabsTrigger>
          <TabsTrigger value="requests">Requests</TabsTrigger>
          <TabsTrigger value="discrepancies">Discrepancies</TabsTrigger>
          <TabsTrigger value="vendor-returns">Vendor returns</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab filters={filters} />
        </TabsContent>
        <TabsContent value="cycle-time">
          <CycleTimeTab filters={filters} />
        </TabsContent>
        <TabsContent value="requests">
          <RequestsTab filters={filters} />
        </TabsContent>
        <TabsContent value="discrepancies">
          <DiscrepanciesTab filters={filters} />
        </TabsContent>
        <TabsContent value="vendor-returns">
          <VendorReturnsTab filters={filters} />
        </TabsContent>
      </Tabs>
    </>
  );
}

// ---------------------------------------------------------------------------
// Overview
// ---------------------------------------------------------------------------

function OverviewTab({ filters }: TabProps) {
  const overview = useTransfersOverview(filters);
  const k = overview.data?.kpis;

  if (overview.isLoading) {
    return (
      <div className="grid gap-3 md:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  }
  if (overview.isError || !k) {
    return (
      <ErrorState
        description="Could not load transfer overview."
        onRetry={() => void overview.refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard
          label="Transfers"
          value={fmt.number(k.transfers)}
          hint={`${k.accepted} accepted`}
        />
        <KPICard
          label="In flight"
          value={fmt.number(k.in_flight)}
          hint={`${k.stuck_over_24h} stuck >24h`}
        />
        <KPICard
          label="Value in transit (cost)"
          value={fmt.npr(k.value_in_transit_at_cost)}
          hint={`${fmt.number(k.units_in_transit)} units · ${fmt.npr(
            k.value_in_transit_at_retail,
          )} retail`}
        />
        <KPICard
          label="Request fulfillment"
          value={pct(k.request_fulfillment_rate)}
          hint={`${k.requests_completed}/${k.requests} completed`}
        />
      </div>
      <div className="grid gap-3 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Transfers by day</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={(overview.data?.by_day ?? []).map((d) => ({
                name: d.day ?? '—',
                value: d.transfers,
              }))}
              xKey="name"
              yKey="value"
            />
          </CardBody>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle>By status</CardTitle>
          </CardHeader>
          <CardBody>
            <BarChart
              data={(overview.data?.by_status ?? []).map((d) => ({
                name: d.status,
                value: d.transfers,
              }))}
              xKey="name"
              yKey="value"
              horizontal
            />
          </CardBody>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Top lanes</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {(overview.data?.by_lane ?? []).length === 0 ? (
            <EmptyState
              icon={<Truck className="h-5 w-5" />}
              title="No transfers"
              description="No transfer lanes in this window."
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">From</th>
                  <th className="px-4 py-2 text-left">To</th>
                  <th className="px-4 py-2 text-right">Transfers</th>
                  <th className="px-4 py-2 text-right">Accepted</th>
                  <th className="px-4 py-2 text-right">In flight</th>
                </tr>
              </thead>
              <tbody>
                {(overview.data?.by_lane ?? []).map((r) => (
                  <tr
                    key={`${r.source_store_id}-${r.destination_store_id}`}
                    className="border-t border-border"
                  >
                    <td className="px-4 py-2">{r.source_store_name}</td>
                    <td className="px-4 py-2">{r.destination_store_name}</td>
                    <td className="px-4 py-2 text-right">{r.transfers}</td>
                    <td className="px-4 py-2 text-right">{r.accepted}</td>
                    <td className="px-4 py-2 text-right">{r.in_flight}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Cycle time
// ---------------------------------------------------------------------------

function CycleTimeTab({ filters }: TabProps) {
  const cycle = useTransfersCycleTime(filters);

  if (cycle.isLoading) return <Skeleton className="h-64" />;
  if (cycle.isError || !cycle.data) {
    return (
      <ErrorState
        description="Could not load cycle time."
        onRetry={() => void cycle.refetch()}
      />
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">{cycle.data.note}</p>
      <div className="grid gap-3 md:grid-cols-3">
        <KPICard
          label="Created → received"
          value={hours(cycle.data.native_medians_hours.created_to_received)}
          hint={`${cycle.data.sample_accepted} accepted`}
        />
        <KPICard
          label="Received → accepted"
          value={hours(cycle.data.native_medians_hours.received_to_accepted)}
        />
        <KPICard
          label="Created → accepted"
          value={hours(cycle.data.native_medians_hours.created_to_accepted)}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Stage medians (status events)</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          <table className="w-full text-xs">
            <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
              <tr>
                <th className="px-4 py-2 text-left">Stage</th>
                <th className="px-4 py-2 text-right">Median hours</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(cycle.data.stage_medians_hours).map(([stage, h]) => (
                <tr key={stage} className="border-t border-border">
                  <td className="px-4 py-2">{stage.replace(/_/g, ' ')}</td>
                  <td className="px-4 py-2 text-right">{hours(h)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Requests
// ---------------------------------------------------------------------------

function RequestsTab({ filters }: TabProps) {
  const requests = useTransfersRequests(filters);

  if (requests.isLoading) return <Skeleton className="h-64" />;
  if (requests.isError || !requests.data) {
    return (
      <ErrorState
        description="Could not load transfer requests."
        onRetry={() => void requests.refetch()}
      />
    );
  }

  const d = requests.data;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard
          label="Requests"
          value={fmt.number(d.kpis.requests)}
          hint={`${d.kpis.pending} pending`}
        />
        <KPICard
          label="Completed"
          value={fmt.number(d.kpis.completed)}
          hint={pct(d.kpis.line_completion_rate) + ' lines'}
        />
        <KPICard
          label="Linked to transfer"
          value={fmt.number(d.kpis.linked_to_transfer)}
          hint={pct(d.kpis.link_coverage) + ' coverage'}
        />
        <KPICard
          label="Units requested"
          value={fmt.number(d.kpis.units_requested)}
          hint={`${d.kpis.lines} lines`}
        />
      </div>
      {d.note ? <p className="text-xs text-muted-foreground">{d.note}</p> : null}
      <Card>
        <CardHeader>
          <CardTitle>Oldest pending</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {d.oldest_pending.length === 0 ? (
            <EmptyState icon={<Truck className="h-5 w-5" />} title="No pending requests" />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">ID</th>
                  <th className="px-4 py-2 text-left">From</th>
                  <th className="px-4 py-2 text-left">To</th>
                  <th className="px-4 py-2 text-left">Created</th>
                </tr>
              </thead>
              <tbody>
                {d.oldest_pending.map((r) => (
                  <tr key={r.id} className="border-t border-border">
                    <td className="px-4 py-2">#{r.id}</td>
                    <td className="px-4 py-2">{r.source_store_name}</td>
                    <td className="px-4 py-2">{r.destination_store_name}</td>
                    <td className="px-4 py-2">
                      {r.created_at ? r.created_at.slice(0, 10) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Discrepancies
// ---------------------------------------------------------------------------

function DiscrepanciesTab({ filters }: TabProps) {
  const discrepancies = useTransfersDiscrepancies(filters);

  if (discrepancies.isLoading) return <Skeleton className="h-64" />;
  if (discrepancies.isError || !discrepancies.data) {
    return (
      <ErrorState
        description="Could not load discrepancies."
        onRetry={() => void discrepancies.refetch()}
      />
    );
  }

  const d = discrepancies.data;
  const truncated = d.transfer_shortfalls.length >= d.detail_limit;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard
          label="Request shortfall lines"
          value={fmt.number(d.kpis.request_shortfall_lines)}
        />
        <KPICard
          label="Request shortfall units"
          value={fmt.number(d.kpis.request_shortfall_units)}
        />
        <KPICard
          label="Transfer shortfall lines"
          value={fmt.number(d.kpis.transfer_shortfall_lines)}
        />
        <KPICard
          label="Transfer shortfall units"
          value={fmt.number(d.kpis.transfer_shortfall_units)}
        />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Transfer shortfalls</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {d.transfer_shortfalls.length === 0 ? (
            <EmptyState icon={<Truck className="h-5 w-5" />} title="No transfer shortfalls" />
          ) : (
            <>
              <table className="w-full text-xs">
                <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2 text-left">Transfer</th>
                    <th className="px-4 py-2 text-left">Product</th>
                    <th className="px-4 py-2 text-right">Declared</th>
                    <th className="px-4 py-2 text-right">Scanned</th>
                    <th className="px-4 py-2 text-right">Shortfall</th>
                  </tr>
                </thead>
                <tbody>
                  {d.transfer_shortfalls.slice(0, 40).map((r) => (
                    <tr key={r.transfer_item_id} className="border-t border-border">
                      <td className="px-4 py-2">{r.identifier}</td>
                      <td className="px-4 py-2">{r.product_name}</td>
                      <td className="px-4 py-2 text-right">{r.declared}</td>
                      <td className="px-4 py-2 text-right">{r.scanned}</td>
                      <td className="px-4 py-2 text-right">{r.shortfall}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {truncated ? (
                <p className="px-4 py-2 text-[11px] text-muted-foreground">
                  Showing the worst {d.detail_limit} lines. KPIs above cover every
                  shortfall in the window.
                </p>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Vendor returns
// ---------------------------------------------------------------------------

function VendorReturnsTab({ filters }: TabProps) {
  const vendorReturns = useTransfersVendorReturns(filters);

  if (vendorReturns.isLoading) return <Skeleton className="h-64" />;
  if (vendorReturns.isError || !vendorReturns.data) {
    return (
      <ErrorState
        description="Could not load vendor returns."
        onRetry={() => void vendorReturns.refetch()}
      />
    );
  }

  const d = vendorReturns.data;

  return (
    <div className="space-y-4">
      <div className="grid gap-3 md:grid-cols-4">
        <KPICard label="Returns" value={fmt.number(d.kpis.returns)} />
        <KPICard label="Lines" value={fmt.number(d.kpis.lines)} />
        <KPICard label="Units" value={fmt.number(d.kpis.units)} />
        <KPICard label="Value" value={fmt.npr(d.kpis.value)} />
      </div>
      <Card>
        <CardHeader>
          <CardTitle>By vendor</CardTitle>
        </CardHeader>
        <CardBody className="overflow-x-auto p-0">
          {d.by_vendor.length === 0 ? (
            <EmptyState icon={<Truck className="h-5 w-5" />} title="No vendor returns" />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-muted/40 text-[10px] uppercase text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 text-left">Vendor</th>
                  <th className="px-4 py-2 text-right">Returns</th>
                  <th className="px-4 py-2 text-right">Units</th>
                  <th className="px-4 py-2 text-right">Value</th>
                </tr>
              </thead>
              <tbody>
                {d.by_vendor.map((r) => (
                  <tr key={r.vendor_id} className="border-t border-border">
                    <td className="px-4 py-2">{r.vendor_name}</td>
                    <td className="px-4 py-2 text-right">{r.returns}</td>
                    <td className="px-4 py-2 text-right">{r.units}</td>
                    <td className="px-4 py-2 text-right">{fmt.npr(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>
    </div>
  );
}
